import React from 'react';
import { TITLE_MIN, TITLE_MAX, isPublishableTitle, titleLengthError } from './title_limits';
import {
  shouldGenerateDefaultImage,
  requestDefaultImage,
  withDefaultImage,
} from './default_image';

const BODY_PLACEHOLDER =
  'イベントの概要、楽しめる点、注意点など\n例）みんなでハロウィーンパーティしましょう！仮装できる人はぜひ仮装してきてください！\n渋谷駅近くのレンタルスペースで行います！\n基本的にみんな一人参加なので、お気軽に参加してくださいね！\n\n●当日の流れ\n18:00 待ち合わせ場所に集合\n18:10 班分けしてみんなで買い出し\n19:00 パーティ開始\n21:00 解散\n\n●持ち物\n仮装するための衣装（する人だけでOK）\n\n●注意事項\n勧誘などはNGです。';

const C = {
  ink: '#333333',
  sub: '#666666',
  muted: '#8B9097',
  line: '#ECECEE',
  primary: '#FF6467',
  wash: '#FFEAEA',
  surface: '#FFFFFF',
  danger: '#E05C35',
};

const card = (error) => ({
  background: C.surface,
  border: `1px solid ${error ? C.danger : C.line}`,
  borderRadius: 16,
  padding: '14px 16px',
  boxShadow: '0 1px 2px rgba(30,30,40,.03), 0 10px 26px -16px rgba(30,30,40,.14)',
});
const headerRow = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 };
const labelText = { fontSize: 12, fontWeight: 800, color: C.sub, letterSpacing: '.02em' };
const reqPill = { fontSize: 10, fontWeight: 800, color: C.primary, background: C.wash, borderRadius: 5, padding: '1px 6px' };
const errText = { color: C.danger, fontSize: 12, margin: '0 0 8px' };
const aiPill = { fontSize: 10, fontWeight: 800, color: C.primary, background: C.wash, borderRadius: 5, padding: '1px 6px' };
const countText = { fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums', marginLeft: 'auto' };
const helpText = { fontSize: 12, color: C.muted, margin: '8px 0 0' };
// 公開に必要なタイトルの字数（validate() と同じ 15〜60）

const SparkleIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2.75c.55 4.45 2.8 6.7 7.25 7.25-4.45.55-6.7 2.8-7.25 7.25-.55-4.45-2.8-6.7-7.25-7.25C9.2 9.45 11.45 7.2 12 2.75Z" fill="currentColor" />
    <path d="M19 15.5c.2 1.65 1.05 2.5 2.7 2.7-1.65.2-2.5 1.05-2.7 2.7-.2-1.65-1.05-2.5-2.7-2.7 1.65-.2 2.5-1.05 2.7-2.7Z" fill="currentColor" />
  </svg>
);

// @context TGW-2195 AIが提案した箇所を、イベント名と紹介文で同じ印にする。
const AiSuggestionPill = () => (
  <span style={{ ...aiPill, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
    <SparkleIcon />
    AIの提案
  </span>
);

const SectionSubject = ({
  event,
  setEvent,
  bodyText,
  titleText,
  setBodyText,
  setTitleText,
  bodyError,
  titleError,
  setTitleError,
  updateTextContent,
  onFocusScrollForAndroid,
  userAgent,
  aiSuggested,
  clearAiSuggested,
}) => {
  const [lastUpdateTime, setLastUpdateTime] = React.useState(0);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  // @context TGW-2195 2行固定の欄だと1行のイベント名が上に寄って見えるため、内容の行数に合わせて高さを変える。
  const titleInputRef = React.useRef(null);
  React.useEffect(() => {
    const el = titleInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [titleText]);
  const [savedAt, setSavedAt] = React.useState(null);

  const updateTextContentToDB = (text) => {
    const currentTime = Date.now();
    if (currentTime - lastUpdateTime >= 20000) {
      updateTextContent && updateTextContent(text);
      setLastUpdateTime(currentTime);
      setSavedAt(currentTime);
    } else {
      return;
    }
  };

  // @context TGW-2195 既定画像の生成判定・要求は default_image.js に集約し、入口（index.js）と共用する。
  const onBlurTitle = React.useCallback((titleText) => {
    if (!shouldGenerateDefaultImage(event, titleText)) return;

    requestDefaultImage(event.id, titleText)
      .then((url) => setEvent(withDefaultImage(url)))
      .catch(() => {});
  }, [event, setEvent]);

  // @context TGW-2195 上限は validate()（60字）と揃える。旧文言の100字は保存時に弾かれる値と食い違っていた。
  const onTitleError = React.useCallback(() => {
    if (!isPublishableTitle(titleText)) {
      setTitleError(titleLengthError);
    } else {
      setTitleError('');
    }
  }, [titleText, setTitleError]);

  const closeEditor = React.useCallback(() => {
    updateTextContent && updateTextContent(bodyText);
    setLastUpdateTime(Date.now());
    setSavedAt(Date.now());
    setIsEditorOpen(false);
  }, [bodyText, updateTextContent]);

  const openEditor = () => {
    setIsEditorOpen(true);
  };

  return (
    <section id="event_details">
      <style>{`.tg-title-input::placeholder{color:#BCC1C6;opacity:1;font-weight:400;}`}</style>

      {/* イベント名 */}
      <div className="container col-md-8 col-lg-6" style={{ paddingTop: 8 }}>
        <div style={card(titleError)}>
          <div style={headerRow}>
            <span style={labelText}>イベント名</span>
            <span style={reqPill}>必須</span>
            {aiSuggested?.title && <AiSuggestionPill />}
            <span style={countText}>
              <b style={{ color: C.sub, fontWeight: 800 }}>{(titleText || '').length}</b> / {TITLE_MAX}
            </span>
          </div>
          {titleError && <p style={errText}>{titleError}</p>}
          <textarea
            id="title"
            aria-label="イベント名"
            ref={titleInputRef}
            className="tg-title-input"
            rows={1}
            maxLength={TITLE_MAX}
            onBlur={() => {
              onTitleError();
              onBlurTitle(titleText);
            }}
            value={titleText}
            onChange={(e) => {
              setTitleText(e.target.value);
              setEvent((prev) => ({ ...prev, title: e.target.value }));
              clearAiSuggested && clearAiSuggested('title');
            }}
            placeholder="例）平日夜の、ゆるっとカレー交流会"
            onFocus={(e) => onFocusScrollForAndroid(e)}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontSize: 16,
              fontWeight: 700,
              color: C.ink,
              background: 'transparent',
              padding: 0,
              lineHeight: 1.5,
              overflow: 'hidden',
              display: 'block',
            }}
          />
          {(titleText || '').length < TITLE_MIN && (
            <p style={helpText}>
              公開には{TITLE_MIN}字以上が必要です（あと {TITLE_MIN - (titleText || '').length} 字）
            </p>
          )}
        </div>
      </div>

      {/* 紹介文 */}
      <div className="container col-md-8 col-lg-6" style={{ paddingTop: 8 }}>
        <div style={card(bodyError)}>
          <div style={headerRow}>
            <span style={labelText}>紹介文</span>
            <span style={reqPill}>必須</span>
            {aiSuggested?.body && <AiSuggestionPill />}
          </div>
          {bodyError && <p style={errText}>{bodyError}</p>}
          <div
            id="body-preview"
            role="button"
            tabIndex={0}
            onClick={openEditor}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openEditor();
            }}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10 }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: bodyText ? C.ink : C.muted,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontWeight: bodyText ? 400 : 400,
              }}
            >
              {bodyText || 'タップして紹介文を入力'}
            </span>
            <span style={{ color: C.muted, flex: 'none', fontSize: 20, lineHeight: 1.2 }}>›</span>
          </div>
        </div>
      </div>

      {isEditorOpen && (
        <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: `1px solid ${C.line}`, flex: 'none' }}>
            <button type="button" onClick={closeEditor} style={{ background: 'none', border: 'none', fontSize: 15, color: C.ink, fontWeight: 600, padding: '4px 8px' }}>
              ‹ 戻る
            </button>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>紹介文</span>
            <button type="button" onClick={closeEditor} style={{ background: 'none', border: 'none', fontSize: 15, color: C.primary, fontWeight: 700, padding: '4px 8px' }}>
              完了
            </button>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '14px 14px 0', minHeight: 0 }}>
            <textarea
              id="body"
              autoFocus
              className="w-100"
              placeholder={BODY_PLACEHOLDER}
              value={bodyText}
              onChange={(e) => {
                setBodyText(e.target.value);
                updateTextContentToDB(e.target.value);
                clearAiSuggested && clearAiSuggested('body');
              }}
              onBlur={() => {
                updateTextContentToDB(bodyText);
              }}
              style={{ flex: 1, width: '100%', border: 'none', outline: 'none', resize: 'none', fontSize: 16, lineHeight: 1.8, color: C.ink, fontFamily: 'inherit', padding: 0 }}
            />
            <p style={{ fontSize: 11, color: C.muted, padding: '8px 2px calc(8px + env(safe-area-inset-bottom))', flex: 'none', margin: 0 }}>
              {savedAt ? '自動保存しました' : '入力内容は自動で保存されます'}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default SectionSubject;
